import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, apiFetch } from "@/lib/api-client";
import { formatDateTime } from "@/lib/i18n";
import {
  createEmptyInspectionDraft,
  InspectionForm,
  type InspectionTemplateDraft,
} from "@/components/pages/bookings/inspection-form";
import {
  InspectionExecution,
  type InspectionBookingOption,
  type InspectionTemplateOption,
  type UploadedInspectionMedia,
} from "@/components/pages/bookings/inspection-execution";

interface InspectionHistorySummary {
  id: string;
  bookingId: string;
  templateId: string;
  templateName: string;
  serviceName: string | null;
  customerName: string;
  completedBy: string | null;
  completedAt: string | null;
  status: string;
  overallResult: string | null;
  flaggedItems: number;
}

interface InspectionHistoryDetail {
  inspection: {
    id: string;
    bookingId: string;
    templateId: string;
    status: string;
    overallResult: string | null;
    notes: string | null;
    completedBy: string | null;
    completedAt: string | null;
    responses: Array<{
      id: string;
      templateItemId: string;
      itemLabel: string;
      itemType: string;
      value: string | null;
      notes: string | null;
      flagged: boolean;
      media: UploadedInspectionMedia | null;
    }>;
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

export default function InspectionsPage() {
  const [templates, setTemplates] = useState<InspectionTemplateOption[]>([]);
  const [bookings, setBookings] = useState<InspectionBookingOption[]>([]);
  const [services, setServices] = useState<Array<{ id: string; name: string }>>([]);
  const [history, setHistory] = useState<InspectionHistorySummary[]>([]);
  const [selectedInspectionId, setSelectedInspectionId] = useState<string | null>(null);
  const [selectedInspection, setSelectedInspection] = useState<InspectionHistoryDetail["inspection"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<InspectionTemplateDraft>(createEmptyInspectionDraft());
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [executingInspection, setExecutingInspection] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"templates" | "execute" | "history">("templates");

  const serviceOptions = useMemo(() => services, [services]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [templateResponse, servicesResponse, bookingResponse, inspectionResponse] = await Promise.all([
        apiFetch<{ templates: InspectionTemplateOption[] }>("/crm/bookings/inspections/templates"),
        apiFetch<{ data: Array<{ id: string; name: string }> }>("/crm/bookings/services/intake"),
        apiFetch<{ bookings: InspectionBookingOption[] }>("/crm/bookings/inspections/bookings"),
        apiFetch<{ inspections: InspectionHistorySummary[] }>("/crm/bookings/inspections"),
      ]);
      setTemplates(templateResponse.templates);
      setServices(servicesResponse.data ?? []);
      setBookings(bookingResponse.bookings);
      setHistory(inspectionResponse.inspections);
      setSelectedInspectionId((current) => current ?? inspectionResponse.inspections[0]?.id ?? null);
    } catch (caughtError) {
      setError(getErrorMessage(caughtError, "Could not load inspection runtime."));
      setTemplates([]);
      setServices([]);
      setBookings([]);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInspectionDetail = useCallback(async (inspectionId: string) => {
    setDetailLoading(true);
    try {
      const response = await apiFetch<InspectionHistoryDetail>(`/crm/bookings/inspections/${inspectionId}`);
      setSelectedInspection(response.inspection);
    } catch (caughtError) {
      setError(getErrorMessage(caughtError, "Could not load inspection detail."));
      setSelectedInspection(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (!selectedInspectionId) return;
    void loadInspectionDetail(selectedInspectionId);
  }, [loadInspectionDetail, selectedInspectionId]);

  async function handleSaveTemplate() {
    setSavingTemplate(true);
    setError(null);
    try {
      await apiFetch("/crm/bookings/inspections/templates", {
        method: "POST",
        body: JSON.stringify({
          name: draft.name,
          description: draft.description || null,
          serviceId: draft.serviceId || null,
          phase: draft.phase,
          isRequired: draft.isRequired,
          items: draft.items.map((item, index) => ({
            label: item.label,
            itemType: item.itemType,
            options: item.itemType === "select" ? item.options : undefined,
            isRequired: item.isRequired,
            sortOrder: index,
            groupName: item.groupName || null,
          })),
        }),
      });
      setDraft(createEmptyInspectionDraft());
      await loadPage();
      setActiveTab("execute");
    } catch (caughtError) {
      setError(getErrorMessage(caughtError, "Could not save inspection template."));
    } finally {
      setSavingTemplate(false);
    }
  }

  async function handleUploadPhoto(bookingId: string, _itemId: string, file: File) {
    setUploadingItemId(_itemId);
    try {
      const formData = new FormData();
      formData.set("file", file);
      formData.set("phase", "during");
      const response = await apiFetch<{ media: UploadedInspectionMedia }>(`/crm/bookings/inspections/${bookingId}/media`, {
        method: "POST",
        body: formData,
      });
      return response.media;
    } finally {
      setUploadingItemId(null);
    }
  }

  async function handleExecuteInspection(input: {
    templateId: string;
    bookingId: string;
    overallResult: "pass" | "fail" | "conditional";
    notes: string;
    responses: Array<{
      templateItemId: string;
      value: string | null;
      notes: string | null;
      flagged: boolean;
      mediaId: string | null;
    }>;
  }) {
    setExecutingInspection(true);
    setError(null);
    try {
      const response = await apiFetch<{ inspection: { id: string } }>(`/crm/bookings/inspections/${input.templateId}/execute`, {
        method: "POST",
        body: JSON.stringify({
          bookingId: input.bookingId,
          overallResult: input.overallResult,
          notes: input.notes || null,
          responses: input.responses,
        }),
      });
      await loadPage();
      setSelectedInspectionId(response.inspection.id);
      setActiveTab("history");
    } catch (caughtError) {
      setError(getErrorMessage(caughtError, "Could not save inspection execution."));
    } finally {
      setExecutingInspection(false);
    }
  }

  if (loading) {
    return <div className="animate-pulse rounded-2xl bg-[var(--color-muted,#e5e7eb)] h-48" />;
  }

  return (
    <div className="space-y-6">
      <div className="max-w-4xl">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-primary)]">Field service inspections</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-[var(--color-foreground)]">Inspection templates, execution, and evidence</h1>
        <p className="mt-3 text-sm leading-6 text-[var(--color-muted-foreground)]">
          This screen now runs on the live booking inspection seam: templates, real booking execution, dedicated photo evidence, and completed inspection history.
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
        </div>
      )}

      <div className="flex gap-2 border-b border-[var(--color-border)]">
        {[
          { id: "templates", label: `Templates (${templates.length})` },
          { id: "execute", label: "Execute" },
          { id: "history", label: `History (${history.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={`-mb-px border-b-2 px-4 py-3 text-sm font-medium ${activeTab === tab.id ? "border-[var(--color-primary)] text-[var(--color-foreground)]" : "border-transparent text-[var(--color-muted-foreground)]"}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "templates" && (
        <InspectionForm
          draft={draft}
          services={serviceOptions}
          saving={savingTemplate}
          onChange={setDraft}
          onSubmit={() => void handleSaveTemplate()}
        />
      )}

      {activeTab === "execute" && (
        <InspectionExecution
          templates={templates}
          bookings={bookings}
          uploadingItemId={uploadingItemId}
          executing={executingInspection}
          onUploadPhoto={handleUploadPhoto}
          onExecute={handleExecuteInspection}
        />
      )}

      {activeTab === "history" && (
        <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
          <div className="space-y-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            {history.length === 0 ? (
              <p className="text-sm text-[var(--color-muted-foreground)]">No completed inspections yet.</p>
            ) : history.map((inspection) => (
              <button
                key={inspection.id}
                type="button"
                onClick={() => setSelectedInspectionId(inspection.id)}
                className={`w-full rounded-xl border p-4 text-left ${selectedInspectionId === inspection.id ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5" : "border-[var(--color-border)]"}`}
              >
                <p className="text-sm font-semibold text-[var(--color-foreground)]">{inspection.templateName}</p>
                <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{inspection.customerName} · {inspection.serviceName ?? "Unknown service"}</p>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  {inspection.completedAt ? formatDateTime(inspection.completedAt) : "In progress"}
                </p>
              </button>
            ))}
          </div>

          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
            {detailLoading ? (
              <div className="animate-pulse rounded-xl bg-[var(--color-muted,#e5e7eb)] h-32" />
            ) : !selectedInspection ? (
              <p className="text-sm text-[var(--color-muted-foreground)]">Select an inspection to review the completed checklist and attached photos.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <h2 className="text-xl font-semibold text-[var(--color-foreground)]">Inspection detail</h2>
                  <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                    Result: {selectedInspection.overallResult ?? "pending"} · {selectedInspection.completedAt ? formatDateTime(selectedInspection.completedAt) : "Not completed"}
                  </p>
                  {selectedInspection.notes && (
                    <p className="mt-3 rounded-xl bg-[var(--color-muted)] px-4 py-3 text-sm text-[var(--color-foreground)]">{selectedInspection.notes}</p>
                  )}
                </div>
                {selectedInspection.responses.map((response) => (
                  <div key={response.id} className="space-y-2 rounded-xl border border-[var(--color-border)] p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-[var(--color-foreground)]">{response.itemLabel}</p>
                      {response.flagged && <span className="rounded-full bg-[var(--color-destructive)]/10 px-2 py-1 text-xs font-medium text-[var(--color-destructive)]">Flagged</span>}
                    </div>
                    {response.value && <p className="text-sm text-[var(--color-foreground)]">{response.value}</p>}
                    {response.notes && <p className="text-sm text-[var(--color-muted-foreground)]">{response.notes}</p>}
                    {response.media && (
                      <img src={response.media.fileUrl} alt={response.itemLabel} className="max-h-64 rounded-lg border border-[var(--color-border)] object-cover" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
